import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './FeedbackSubmissions.css';

const API_BASE_URL = '/api';

// Icon Components
const ViewIcon = () => (
  <svg width="18px" height="18px" strokeWidth="1.4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M3 13C6.6 5 17.4 5 21 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
    <path d="M12 17C10.3431 17 9 15.6569 9 14C9 12.3431 10.3431 11 12 11C13.6569 11 15 12.3431 15 14C15 15.6569 13.6569 17 12 17Z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const CloseIcon = () => (
  <svg width="20px" height="20px" strokeWidth="2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="currentColor">
    <path d="M6 18L18 6M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
  </svg>
);

const StarIcon = ({ filled }) => (
  <svg width="16px" height="16px" viewBox="0 0 24 24" fill={filled ? "#fbbf24" : "none"} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function FeedbackSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterRating, setFilterRating] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Statistics states
  const [stats, setStats] = useState({
    total: 0,
    avgRating: 0,
    thisMonth: 0,
    lastSevenDays: 0,
    distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Form states
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);

  // Notification states
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  useEffect(() => {
    fetchSubmissions();
    fetchStats();
  }, []);

  useEffect(() => {
    filterSubmissions();
    calculateFilteredStats();
  }, [submissions, filterRating, searchTerm, dateFrom, dateTo]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/contact/submissions?limit=1000`, {
        withCredentials: true
      });

      if (response.data.success) {
        setSubmissions(response.data.data.submissions);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      showNotification('Failed to load feedback submissions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/contact/feedback-stats`, {
        withCredentials: true
      });

      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const filterSubmissions = () => {
    let filtered = [...submissions];

    // Filter only feedback type
    filtered = filtered.filter(sub => sub.type === 'feedback');

    // Filter by rating
    if (filterRating !== 'all') {
      filtered = filtered.filter(sub => sub.rating === parseInt(filterRating));
    }

    // Filter by date range
    if (dateFrom) {
      filtered = filtered.filter(sub => {
        const subDate = new Date(sub.created_at);
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        return subDate >= fromDate;
      });
    }

    if (dateTo) {
      filtered = filtered.filter(sub => {
        const subDate = new Date(sub.created_at);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        return subDate <= toDate;
      });
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(sub =>
        sub.name.toLowerCase().includes(search) ||
        sub.email.toLowerCase().includes(search)
      );
    }

    setFilteredSubmissions(filtered);
    setCurrentPage(1);
  };

  const calculateFilteredStats = () => {
    let filtered = [...submissions];

    // Filter only feedback type
    filtered = filtered.filter(sub => sub.type === 'feedback');

    // Apply date range filter (same as table filter)
    if (dateFrom) {
      filtered = filtered.filter(sub => {
        const subDate = new Date(sub.created_at);
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        return subDate >= fromDate;
      });
    }

    if (dateTo) {
      filtered = filtered.filter(sub => {
        const subDate = new Date(sub.created_at);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        return subDate <= toDate;
      });
    }

    // Calculate statistics from filtered data
    const total = filtered.length;
    const avgRating = total > 0
      ? filtered.reduce((sum, item) => sum + item.rating, 0) / total
      : 0;

    // This month count
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const thisMonth = filtered.filter(item => new Date(item.created_at) >= startOfMonth).length;

    // Last 7 days count
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const lastSevenDays = filtered.filter(item => new Date(item.created_at) >= sevenDaysAgo).length;

    // Rating distribution
    const distribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    filtered.forEach(item => {
      if (item.rating >= 1 && item.rating <= 5) {
        distribution[item.rating]++;
      }
    });

    setStats({
      total,
      avgRating,
      thisMonth,
      lastSevenDays,
      distribution
    });
  };

  const handleViewSubmission = async (id) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/contact/submissions/${id}`, {
        withCredentials: true
      });

      if (response.data.success) {
        setSelectedSubmission(response.data.data);
        setNoteText('');
        setShowViewModal(true);
      }
    } catch (error) {
      console.error('Error fetching submission details:', error);
      showNotification('Failed to load submission details', 'error');
    }
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setSelectedSubmission(null);
    setNoteText('');
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) {
      showNotification('Please enter a note', 'error');
      return;
    }

    try {
      setAddingNote(true);
      const response = await axios.post(
        `${API_BASE_URL}/admin/contact/submissions/${selectedSubmission.submission.id}/notes`,
        { note: noteText },
        { withCredentials: true }
      );

      if (response.data.success) {
        showNotification('Note added successfully');
        setNoteText('');
        await handleViewSubmission(selectedSubmission.submission.id);
      }
    } catch (error) {
      console.error('Error adding note:', error);
      showNotification('Failed to add note', 'error');
    } finally {
      setAddingNote(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map(star => (
          <StarIcon key={star} filled={star <= rating} />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSubmissions.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);

  return (
    <div className="feedback-submissions-container">
      {/* Notification */}
      {notification.show && (
        <div className={`notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div className="feedback-submissions-header">
        <div className="header-left">
          <h1 className="page-title">Feedback Submissions</h1>
          <p className="page-description">Manage and review user feedback with ratings</p>
        </div>
        <div className="header-right">
          <select
            className="filter-select"
            value={filterRating}
            onChange={(e) => setFilterRating(e.target.value)}
          >
            <option value="all">All Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
          <input
            type="date"
            className="date-input"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            placeholder="From Date"
          />
          <input
            type="date"
            className="date-input"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            placeholder="To Date"
          />
          <input
            type="text"
            placeholder="Search by name, email..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Submissions</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Average Rating</div>
          <div className="stat-value">
            {stats.avgRating.toFixed(1)} {renderStars(Math.round(stats.avgRating))}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">This Month</div>
          <div className="stat-value">{stats.thisMonth}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Last 7 Days</div>
          <div className="stat-value">{stats.lastSevenDays}</div>
        </div>
      </div>

      {/* Rating Distribution */}
      <div className="rating-distribution">
        <h3 className="section-title">Rating Distribution</h3>
        <div className="distribution-bars">
          {[5, 4, 3, 2, 1].map(rating => {
            const count = stats.distribution[rating] || 0;
            const percentage = stats.total > 0 ? (count / stats.total) * 100 : 0;
            return (
              <div key={rating} className="distribution-row">
                <div className="rating-label">
                  {rating} {renderStars(rating)}
                </div>
                <div className="bar-container">
                  <div
                    className="bar-fill"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <div className="rating-stats">
                  <span className="count">{count}</span>
                  <span className="percentage">({percentage.toFixed(0)}%)</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-state">Loading feedback submissions...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="feedback-submissions-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Rating</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-state">
                      No feedback submissions found
                    </td>
                  </tr>
                ) : (
                  currentItems.map(sub => (
                    <tr key={sub.id}>
                      <td className="id-cell">#{sub.id}</td>
                      <td>{sub.name}</td>
                      <td className="email-cell">{sub.email}</td>
                      <td>{renderStars(sub.rating)}</td>
                      <td>{formatDate(sub.created_at)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="action-btn view-btn"
                            onClick={() => handleViewSubmission(sub.id)}
                            title="View Details"
                          >
                            <ViewIcon />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {/* View Modal */}
      {showViewModal && selectedSubmission && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Feedback Submission #{selectedSubmission.submission.id}</h2>
              <button className="close-btn" onClick={handleCloseModal}>
                <CloseIcon />
              </button>
            </div>
            <div className="modal-body">
              {/* User Information */}
              <div className="detail-section">
                <h3 className="section-title">User Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Name:</label>
                    <span>{selectedSubmission.submission.name}</span>
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{selectedSubmission.submission.email}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone:</label>
                    <span>{selectedSubmission.submission.phone || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Date:</label>
                    <span>{formatDateTime(selectedSubmission.submission.created_at)}</span>
                  </div>
                  <div className="info-item">
                    <label>Rating:</label>
                    <span>{renderStars(selectedSubmission.submission.rating)}</span>
                  </div>
                </div>
              </div>

              {/* Feedback Message */}
              <div className="detail-section">
                <h3 className="section-title">Feedback Message</h3>
                <div className="message-box">
                  {selectedSubmission.submission.message}
                </div>
              </div>

              {/* Internal Notes */}
              <div className="detail-section">
                <h3 className="section-title">Internal Notes</h3>
                <textarea
                  rows="3"
                  placeholder="Add internal note..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  className="form-textarea"
                />
                <button
                  onClick={handleAddNote}
                  disabled={addingNote || !noteText.trim()}
                  className="primary-btn"
                >
                  {addingNote ? 'Adding...' : 'Add Note'}
                </button>

                {selectedSubmission.notes.length > 0 && (
                  <div className="notes-list">
                    <h4>Previous Notes:</h4>
                    {selectedSubmission.notes.map(note => (
                      <div key={note.id} className="note-item">
                        <div className="note-meta">
                          {formatDateTime(note.created_at)} - {note.created_by}
                        </div>
                        <div className="note-text">{note.note}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Activity Log */}
              <div className="detail-section">
                <h3 className="section-title">Activity Log</h3>
                {selectedSubmission.activity_log.length === 0 ? (
                  <p className="empty-text">No activity yet</p>
                ) : (
                  <div className="activity-list">
                    {selectedSubmission.activity_log.map(log => (
                      <div key={log.id} className="activity-item">
                        <div className="activity-meta">
                          {formatDateTime(log.created_at)}
                          {log.created_by && ` - ${log.created_by}`}
                        </div>
                        <div className="activity-text">
                          <strong>{log.action.replace(/_/g, ' ')}</strong>
                          {log.details && ` - ${log.details}`}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FeedbackSubmissions;
